// AuthContext.js - MINIMAL CHANGE - Only adding password reset functions
import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const profileFetchedRef = useRef(false)
  const currentUserIdRef = useRef(null)

  useEffect(() => {
    let mounted = true

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      
      console.log('Initial session check:', session?.user?.email || 'none')
      const sessionUser = session?.user ?? null
      setUser(sessionUser)
      currentUserIdRef.current = sessionUser?.id || null
      
      if (sessionUser && !profileFetchedRef.current) {
        fetchProfile(sessionUser.id)
      } else {
        setLoading(false)
        setInitialized(true)
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return
      
      console.log('Auth event:', event, session?.user?.email || 'none')
      const sessionUser = session?.user ?? null
      const newUserId = sessionUser?.id || null
      
      // Handle different auth events
      if (event === 'SIGNED_IN') {
        console.log('User signed in:', sessionUser?.email)
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed for:', sessionUser?.email)
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out, resetting state and redirecting')
        // Reset all state
        setProfile(null)
        profileFetchedRef.current = false
        
        // Redirect to auth page immediately after logout
        window.history.pushState({}, '', '/')
        window.dispatchEvent(new PopStateEvent('popstate'))
      } else if (event === 'PASSWORD_RECOVERY') {
        // NEW: Handle password recovery event
        console.log('Password recovery event detected')
      }
      
      // Only update if user actually changed
      if (currentUserIdRef.current !== newUserId) {
        setUser(sessionUser)
        currentUserIdRef.current = newUserId
        profileFetchedRef.current = false // Reset profile fetch flag
        
        if (sessionUser) {
          fetchProfile(sessionUser.id)
        } else {
          setProfile(null)
          setLoading(false)
          setInitialized(true)
        }
      } else if (sessionUser && !profileFetchedRef.current) {
        // User is the same but we haven't fetched profile yet
        fetchProfile(sessionUser.id)
      }
    })

    // Safety timeout
    const safetyTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.log('Safety timeout reached, forcing loading to false')
        setLoading(false)
        setInitialized(true)
      }
    }, 10000) // Increased to 10 seconds

    return () => {
      mounted = false
      subscription.unsubscribe()
      clearTimeout(safetyTimeout)
    }
  }, []) // Empty dependency array - only run once

  const fetchProfile = async (userId) => {
    if (profileFetchedRef.current) {
      console.log('Profile already fetched, skipping')
      return
    }

    try {
      profileFetchedRef.current = true
      console.log('Fetching profile for user:', userId)
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error && error.code === 'PGRST116') {
        console.log('Profile not found, this is normal for email confirmation flow')
        // Don't create profile here - let the trigger handle it
        setProfile({
          id: userId,
          email: user?.email,
          full_name: user?.user_metadata?.full_name || user?.email || '',
        })
      } else if (error) {
        console.error('Error fetching profile:', error)
        setProfile({
          id: userId,
          email: user?.email,
          full_name: user?.user_metadata?.full_name || user?.email || '',
        })
      } else {
        console.log('Profile found:', data)
        setProfile(data)
      }
    } catch (error) {
      console.error('Error in fetchProfile:', error)
      setProfile({
        id: userId,
        email: user?.email,
        full_name: user?.user_metadata?.full_name || user?.email || '',
      })
    } finally {
      console.log('Profile fetch completed')
      setLoading(false)
      setInitialized(true)
    }
  }

  const signUp = async (email, password, fullName, tenantName) => {
    try {
      console.log('Signing up user:', email, 'with tenant:', tenantName)
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            tenant_name: tenantName,
          },
        },
      })

      if (authError) throw authError

      console.log('User signup initiated:', authData.user?.email)

      return { 
        data: authData, 
        error: null,
        message: 'Please check your email to confirm your account before signing in.'
      }
    } catch (error) {
      console.error('Signup error:', error)
      return { data: null, error: error.message }
    }
  }

  const signIn = async (email, password) => {
    try {
      console.log('Signing in user:', email)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        console.error('Sign in error:', error)
        
        // Provide more specific error messages
        if (error.message.includes('Email not confirmed')) {
          throw new Error('Please check your email and click the confirmation link before signing in.')
        } else if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please check your credentials and try again.')
        }
        
        throw error
      } else {
        console.log('Sign in successful:', data.user?.email)
      }
      
      return { data, error: null }
    } catch (error) {
      console.error('Sign in error:', error)
      return { data: null, error: error.message }
    }
  }

  const signOut = async () => {
    try {
      console.log('Signing out user')
      profileFetchedRef.current = false // Reset profile fetch flag
      currentUserIdRef.current = null
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error: error.message }
    }
  }

  // NEW: Password reset functionality - ONLY ADDITIONS FROM HERE
  const resetPassword = async (email) => {
    try {
      console.log('Sending password reset email to:', email)
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      
      if (error) throw error
      
      console.log('Password reset email sent successfully')
      return { 
        data, 
        error: null,
        message: 'Password reset email sent! Please check your inbox for instructions.'
      }
    } catch (error) {
      console.error('Password reset error:', error)
      return { 
        data: null, 
        error: error.message 
      }
    }
  }

  // NEW: Update password (for when user clicks reset link)
  const updatePassword = async (newPassword) => {
    try {
      console.log('Updating user password')
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })
      
      if (error) throw error
      
      console.log('Password updated successfully')
      return { 
        data, 
        error: null,
        message: 'Password updated successfully! You can now sign in with your new password.'
      }
    } catch (error) {
      console.error('Password update error:', error)
      return { 
        data: null, 
        error: error.message 
      }
    }
  }

  const value = {
    user,
    profile,
    loading,
    initialized,
    signUp,
    signIn,
    signOut,
    resetPassword,      // NEW
    updatePassword,     // NEW
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}